/**
 * Step 4 of the Production Line journey (production-lines): the four-stage
 * ladder of a video being made, read from the run's merchant stage and
 * state (a held run shows nothing it did not reach as complete). Moved
 * verbatim out of ProductionLineDetailPage.tsx.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { MerchantCreativeRunView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { formatRelative } from '../../merchant/model.js';
import {
  Card,
  Eyebrow,
  Poster,
  StageList,
  Tide,
  type StageItem,
} from '../../merchant/ui/primitives.js';

/** Each merchant stage's position on the four-step ladder; DONE is past it. */
const STAGE_INDEX: Readonly<
  Record<NonNullable<MerchantCreativeRunView['merchantStage']>, number>
> = {
  SCRIPT: 0,
  VISUALS: 1,
  ASSEMBLY: 2,
  QUALITY: 3,
  DONE: 4,
};

export function GeneratingStep({
  copy,
  language,
  workspaceName,
  run,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly run: MerchantCreativeRunView;
}): ReactNode {
  // The server says how far the run has got, in the wireframe's four stages;
  // the state alone cannot, because a held run reads as QUALITY_REVIEW
  // whatever stage it stopped at. A held run shows the steps before its stage
  // as complete, its stage as paused, and nothing after it as started. When
  // the server cannot say where it stopped, nothing is shown as complete.
  const held = run.merchantState === 'QUALITY_REVIEW';
  const reached =
    run.merchantStage !== null && run.merchantStage !== undefined
      ? STAGE_INDEX[run.merchantStage]
      : held
        ? null
        : run.merchantState === 'PREPARING'
          ? 0
          : run.merchantState === 'CREATING'
            ? 1
            : run.merchantState === 'CHECKING_QUALITY'
              ? 3
              : 4;
  const stages = copy.line.genStages.map((stage, index): StageItem => {
    if (reached === null) {
      return {
        key: String(index),
        title: stage.title,
        desc: stage.desc,
        status: 'queued',
        meta: copy.line.queued,
      };
    }
    const status: StageItem['status'] =
      index < reached ? 'complete' : index === reached && !held ? 'active' : 'queued';
    const meta =
      index < reached
        ? copy.line.complete
        : index === reached
          ? held
            ? copy.line.held
            : copy.line.inProgress
          : copy.line.queued;
    return { key: String(index), title: stage.title, desc: stage.desc, status, meta };
  });
  return (
    <div className="mj-ws-g2 is-journey">
      <Card as="section" padding={24} aria-labelledby="gn-h" aria-live="polite">
        <Eyebrow>
          <Tide />
          {copy.line.step4}
        </Eyebrow>
        <h2 id="gn-h" className="mj-ws-h2 mj-ws-mt-8">
          {run.card?.direction ?? copy.line.step4}
        </h2>
        <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4 }}>
          {run.merchantState === 'QUALITY_REVIEW'
            ? copy.line.heldBody
            : copy.line.genBody(workspaceName)}
        </p>
        <StageList stages={stages} />
        <div className="mj-ws-muted mj-ws-xs mj-ws-row mj-ws-mt-16" style={{ gap: 12 }}>
          <span>
            {copy.common.started(
              formatRelative(run.confirmedAt ?? run.authorizedAt, copy, language),
            )}
          </span>
          <span aria-hidden="true">·</span>
          <Link to="/production-lines">{copy.common.backToLines}</Link>
        </div>
      </Card>
      <Card as="aside" className="mj-ws-preview-aside" padding={24}>
        <Poster width={180} state="generating" label={copy.line.previewSoon} />
      </Card>
    </div>
  );
}
